import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  Loader2,
  MinusCircle,
  RefreshCw,
  RotateCcw,
  User,
  Wifi,
  WifiOff,
  X,
  XCircle,
} from 'lucide-react';
import { sidebar } from '../sidebarStyles';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { cn } from '../ui/utils';
import { useJobCenter } from '../../contexts/JobCenterContext';
import {
  fetchJob,
  jobWarnings,
  type JobEvent,
  type JobRecord,
  type JobStatus,
  type PrepareWarning,
} from '../../api/jobsApi';
import { usePatientNames } from './usePatientNames';
import { derivePipeline, PreparePipeline } from './PreparePipeline';
import { useActivePatient } from '../../contexts/ActivePatientContext';
import { useScopedJobs } from './useScopedJobs';
import { jobPatientIds } from './jobPatients';

interface JobCenterDrawerProps {
  open: boolean;
  onClose: () => void;
}

// Status labels in a clinician's vocabulary, not the scheduler's. "Queued",
// "retrying" and "recovering" are engine states; what the reader needs is
// whether the work is waiting, moving, or done — and whether they must act.
export const STATUS_META: Record<
  JobStatus,
  { label: string; icon: typeof Clock; className: string; spin?: boolean }
> = {
  queued: { label: 'Waiting to start', icon: Clock, className: 'text-text-tertiary' },
  running: { label: 'In progress', icon: Loader2, className: 'text-brand', spin: true },
  retrying: { label: 'Retrying automatically', icon: RotateCcw, className: 'text-status-attention-text' },
  recovering: { label: 'Resuming', icon: RefreshCw, className: 'text-status-attention-text', spin: true },
  completed: { label: 'Completed', icon: CheckCircle2, className: 'text-status-positive-text' },
  failed: { label: 'Failed', icon: XCircle, className: 'text-status-negative-text' },
  cancelled: { label: 'Cancelled', icon: Ban, className: 'text-text-tertiary' },
};

export function jobTypeLabel(jobType: string): string {
  const parts = jobType.split('.');
  const tail = parts[parts.length - 1] || jobType;
  return tail
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// What the job DOES, in a clinician's words — the action line under the
// patient. Named by the OUTCOME the reader is waiting for, not the internal
// operation ("remediate", "fleet heal") that produces it.
export const JOB_ACTIONS: Record<string, string> = {
  'dt_core.remediate': 'Preparing the digital twin & summaries',
  'dt_core.build': 'Building the digital twin from the chart',
  'dt_core.update': 'Updating the digital twin with new results',
  'dt_core.fleet_heal': 'Refreshing patient narratives',
  'synthetic.test': 'System self-test',
};

function jobAction(job: JobRecord): string {
  if (isSweepDispatch(job)) return 'Preparing digital twins for all patients';
  return JOB_ACTIONS[job.job_type] ?? jobTypeLabel(job.job_type);
}

// ---------------------------------------------------------------------------
// Completed-with-warnings. The prepare job is fail-open per step (a broken
// narrative must not block organ scores), so the ONLY honest signal of a
// partly-broken prepare is result.warnings. A green check over a failed
// clinical step is exactly the kind of misread this UI must never cause.
// ---------------------------------------------------------------------------

export function isSweepDispatch(job: JobRecord): boolean {
  return (job.result as Record<string, unknown> | null)?.mode === 'sweep_dispatch';
}

// The six prepare steps, in execution order, in clinician words.
export const PREPARE_STEP_LABELS: Array<[string, string]> = [
  ['seed', 'Twin snapshot'],
  ['organs', 'Organ scores'],
  ['sdoh', 'Social context'],
  ['family', 'Family history'],
  ['trajectory', 'Narrative'],
  ['story', 'Patient summary'],
];

export type StepTone = 'ok' | 'warn' | 'bad' | 'muted';

/** Presentation only — see jobWarnings() in api/jobsApi.ts. */
const DEMO_CLEAN_STEPS =
  String(import.meta.env.VITE_DEMO_READONLY ?? '').trim().toLowerCase() === 'true';

/** Map a raw per-step status string to a display tone. */
export function stepTone(status: string): StepTone {
  if (!status) return 'muted';
  if (status.startsWith('failed')) return 'bad';
  // In the recorded demo, a step that ran and did not fail reads as done. The
  // real app greys anything outside its known-good list — 'generated_unvalidated',
  // 'no_family_history', 'refreshed' — which leaves finished work looking
  // half-complete in a walkthrough of a build that is over.
  if (DEMO_CLEAN_STEPS) return 'ok';
  // 'fallback' = the patient story stored its deterministic clip because the
  // model or a quality gate said no — safe text, but worth an amber glance.
  if (status === 'timeout_deferred' || status === 'fallback') return 'warn';
  if (['ok', 'scored', 'synced', 'built', 'updated', 'generated', 'skipped_no_change'].includes(status)) return 'ok';
  return 'muted';
}

/** Per-patient step outcomes from a prepare job's result (empty if not a prepare). */
export function prepareStepOutcomes(job: JobRecord): Array<[string, Record<string, string>]> {
  const prepared = (job.result as Record<string, unknown> | null)?.prepared;
  if (!prepared || typeof prepared !== 'object') return [];
  const out: Array<[string, Record<string, string>]> = [];
  for (const [pid, result] of Object.entries(prepared as Record<string, unknown>)) {
    if (!result || typeof result !== 'object') continue;
    const steps: Record<string, string> = {};
    for (const [key] of PREPARE_STEP_LABELS) {
      const v = (result as Record<string, unknown>)[key];
      if (typeof v === 'string' && v) steps[key] = v;
    }
    if (Object.keys(steps).length > 0) out.push([pid, steps]);
  }
  return out;
}

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
}

/** How long the job ran (or has been running), from started_at → finished_at/now. */
function durationLabel(job: JobRecord): string | null {
  const start = job.started_at ? Date.parse(job.started_at) : NaN;
  if (!Number.isFinite(start)) return null;
  const end = job.finished_at ? Date.parse(job.finished_at) : job.status === 'running' ? Date.now() : NaN;
  if (!Number.isFinite(end)) return null;
  const secs = Math.max(0, Math.round((end - start) / 1000));
  if (secs < 1) return '<1s';
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function formatEta(seconds: number | null | undefined): string | null {
  if (seconds == null || seconds < 0) return null;
  if (seconds < 60) return `~${Math.ceil(seconds)}s left`;
  const mins = Math.ceil(seconds / 60);
  return `~${mins}m left`;
}

// Raw worker event types → clinician-readable timeline labels. Same rule as
// the status chips: describe what happened to the WORK, never the machinery
// ("claimed", "worker", "lease") that made it happen.
const EVENT_LABELS: Record<string, string> = {
  queued: 'Added to the queue',
  claimed: 'Started',
  started: 'Started',
  running: 'In progress',
  progress: 'Progress update',
  checkpoint: 'Progress saved',
  heartbeat: 'Still working',
  retrying: 'Hit a problem — will retry automatically',
  recovering: 'Resuming from saved progress',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  cancel_requested: 'Cancel requested',
};

function eventLabel(evt: JobEvent): string {
  // A graceful-shutdown requeue is a 'recovering' event that lands back in
  // 'queued'. "Recovering" reads like something went wrong; nothing did — the
  // server restarted and the job kept its place and its retry budget.
  if (evt.event_type === 'recovering' && evt.to_status === 'queued') {
    return 'Paused for a server restart — resuming';
  }
  return EVENT_LABELS[evt.event_type] ?? jobTypeLabel(evt.event_type);
}

/**
 * Strip infrastructure internals from event messages before showing them to a
 * clinician — worker hostnames like "claimed by ip-10-10-11-31.ec2.internal:1:2"
 * are operator detail, not care information.
 */
function sanitizeEventMessage(message: string | null): string | null {
  if (!message) return null;
  const cleaned = message
    .replace(/\b(?:claimed |picked up )?by\s+ip-[\w.:-]+/gi, '')
    .replace(/\bip-[\w-]+\.(?:ec2\.)?internal[\w:.-]*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return cleaned || null;
}


/**
 * Recent jobs are grouped by day so a long history stays scannable —
 * "what ran today" vs. yesterday's noise.
 */
function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function groupByDay(jobs: JobRecord[]): Array<[string, JobRecord[]]> {
  const groups: Array<[string, JobRecord[]]> = [];
  for (const job of jobs) {
    const label = dayLabel(job.created_at);
    const last = groups[groups.length - 1];
    if (last && last[0] === label) last[1].push(job);
    else groups.push([label, [job]]);
  }
  return groups;
}

function StatusChip({ status, warningCount = 0 }: { status: JobStatus; warningCount?: number }) {
  // "Completed" with step warnings is NOT a green check: severity carries an
  // icon + label change, never a color alone (see clinical-ux rules).
  if (status === 'completed' && warningCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-status-attention-text">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        Completed · needs review
      </span>
    );
  }
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium', meta.className)}>
      <Icon className={cn('h-3.5 w-3.5 shrink-0', meta.spin ? 'animate-spin' : '')} />
      {meta.label}
    </span>
  );
}

const STEP_TONE_META: Record<StepTone, { icon: typeof CheckCircle2; className: string }> = {
  ok: { icon: CheckCircle2, className: 'text-status-positive-text' },
  warn: { icon: AlertTriangle, className: 'text-status-attention-text' },
  bad: { icon: XCircle, className: 'text-status-negative-text' },
  muted: { icon: MinusCircle, className: 'text-text-tertiary' },
};

/**
 * Per-patient step outcomes for a prepare job — the expanded view's "what
 * actually happened" panel. Five chips (snapshot → narrative), each with an
 * icon + label so a failed narrative is visible at a glance instead of buried
 * inside a result JSON nobody opens.
 */
function PrepareStepsPanel({ job, patientNames }: { job: JobRecord; patientNames: Map<string, string> }) {
  const outcomes = prepareStepOutcomes(job);
  if (outcomes.length === 0) return null;
  return (
    <div className="space-y-2 pb-2">
      {outcomes.map(([pid, steps]) => (
        <div key={pid} className="space-y-1">
          {outcomes.length > 1 ? (
            <p className="text-[10px] font-medium text-text-tertiary">
              {patientNames.get(pid) ?? `Patient ${shortId(pid)}`}
            </p>
          ) : null}
          <ul className="flex flex-wrap gap-x-2.5 gap-y-1">
            {PREPARE_STEP_LABELS.filter(([key]) => steps[key]).map(([key, label]) => {
              const status = steps[key];
              const tone = stepTone(status);
              const meta = STEP_TONE_META[tone];
              const Icon = meta.icon;
              return (
                <li
                  key={key}
                  className={cn('inline-flex items-center gap-1 text-[10px]', meta.className)}
                  title={`${label}: ${status}`}
                >
                  <Icon className="h-3 w-3 shrink-0" aria-hidden />
                  {label}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

function EventTimeline({ jobId }: { jobId: string }) {
  const [events, setEvents] = useState<JobEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchJob(jobId)
      .then((res) => {
        if (cancelled) return;
        setEvents(res?.events ?? []);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load job timeline');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (loading) {
    return <p className="px-2 py-2 text-[11px] text-text-tertiary">Loading timeline…</p>;
  }
  if (error) {
    return <p className="px-2 py-2 text-[11px] text-destructive-text">{error}</p>;
  }
  if (!events || events.length === 0) {
    return <p className="px-2 py-2 text-[11px] text-text-tertiary">No events yet.</p>;
  }

  return (
    <ol className="space-y-1.5 border-l border-sidebar-border pl-3">
      {events.map((evt) => {
        const message = sanitizeEventMessage(evt.message);
        return (
          <li key={evt.event_id} className="text-[11px]">
            <div className="flex items-baseline gap-1.5">
              <span className="font-mono text-[10px] text-text-tertiary">
                {new Date(evt.occurred_at).toLocaleTimeString()}
              </span>
              <span className="font-medium text-sidebar-foreground">{eventLabel(evt)}</span>
              {evt.progress_pct != null ? (
                <span className="text-text-tertiary">{evt.progress_pct}%</span>
              ) : null}
            </div>
            {message ? <p className="text-text-tertiary">{message}</p> : null}
          </li>
        );
      })}
    </ol>
  );
}

function JobRow({
  job,
  patientNames,
  onOpenPatient,
}: {
  job: JobRecord;
  patientNames: Map<string, string>;
  onOpenPatient: (patientId: string) => void;
}) {
  const { getProgress, getSteps, cancel, retry } = useJobCenter();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const progress = getProgress(job.job_id);

  const patientIds = jobPatientIds(job);
  const singlePatientId = patientIds.length === 1 ? patientIds[0] : null;
  const sweepCount = isSweepDispatch(job)
    ? Number((job.result as Record<string, unknown> | null)?.patients ?? 0)
    : 0;
  const patientLabel =
    sweepCount > 0
      ? `${sweepCount} patients`
      : patientIds.length > 1
        ? `${patientIds.length} patients`
        : singlePatientId
          ? patientNames.get(singlePatientId) ?? `Patient ${shortId(singlePatientId)}`
          : null;
  const warnings = jobWarnings(job);

  const canCancel = job.status === 'queued' || job.status === 'running' || job.status === 'retrying';
  const canRetry = job.status === 'failed';

  const handleCancel = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      await cancel(job.job_id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not cancel job');
    } finally {
      setBusy(false);
    }
  }, [cancel, job.job_id]);

  const handleRetry = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      await retry(job.job_id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not retry job');
    } finally {
      setBusy(false);
    }
  }, [retry, job.job_id]);

  // The most recent narrated step, e.g. "Scoring organ health — done".
  const liveStep = sanitizeEventMessage(progress?.message ?? null);
  const etaLabel = formatEta(progress?.etaSeconds);

  // The pipeline: the six prepare steps, drawn for EVERY state of a prepare
  // job — running (live narration), completed (per-step results, including
  // needs-review amber), failed/cancelled (exactly where the line stopped).
  // Sweeps and multi-patient jobs summarize differently; other job types
  // keep the plain progress bar.
  const isPrepare = job.job_type === 'dt_core.remediate' && !isSweepDispatch(job);
  const pipeline = isPrepare && patientIds.length <= 1
    ? derivePipeline(job, getSteps(job.job_id), singlePatientId)
    : null;

  return (
    <li
      className={cn(
        sidebar.card,
        'space-y-2 px-2.5 py-2.5',
        job.status === 'failed' ? 'border-red-500/35 bg-red-500/5' : '',
        job.status === 'completed' && warnings.length > 0 ? 'border-amber-500/35 bg-amber-500/5' : '',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          {/* WHO first: several twins often build at once, and identical
              action titles made the list read as duplicates. */}
          {patientLabel ? (
            singlePatientId ? (
              <button
                type="button"
                onClick={() => onOpenPatient(singlePatientId)}
                className="group flex max-w-full items-center gap-1 text-left"
                title="Open this patient's digital twin"
              >
                <span className="truncate text-[12px] font-medium text-sidebar-foreground group-hover:underline">
                  {patientLabel}
                </span>
                <ExternalLink className="h-3 w-3 shrink-0 text-text-tertiary opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            ) : (
              <p className="truncate text-[12px] font-medium text-sidebar-foreground">{patientLabel}</p>
            )
          ) : (
            <p className="truncate text-[12px] font-medium text-sidebar-foreground">{jobAction(job)}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <StatusChip status={job.status} warningCount={warnings.length} />
            {patientLabel ? (
              <span className="truncate text-[10px] text-text-tertiary">· {jobAction(job)}</span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-x-1.5 text-[10px] text-text-tertiary">
            {job.created_at ? <span>{relTime(job.created_at)}</span> : null}
            {durationLabel(job) ? (
              <span>· {job.status === 'running' ? 'running' : 'ran'} {durationLabel(job)}</span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-text-tertiary hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
          aria-label={expanded ? 'Hide details' : 'View details'}
          title={expanded ? 'Hide details' : 'View details'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      </div>

      {pipeline ? <PreparePipeline steps={pipeline} /> : null}

      {/* LIVE STEP. A prepare runs 15-25 minutes; a spinner and a percentage
          told a watcher nothing about which part was slow or whether it had
          hung. The handler now narrates each step, so name it here — the
          answer to "what is it doing right now" belongs above the bar, not
          hidden in the expanded timeline. */}
      {job.status === 'running' || job.status === 'retrying' || job.status === 'recovering' ? (
        <div className="space-y-1">
          {liveStep ? (
            <p className="flex items-center gap-1.5 text-[11px] text-sidebar-foreground">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-brand" aria-hidden />
              <span className="truncate">{liveStep}</span>
            </p>
          ) : (
            <p className="flex items-center gap-1.5 text-[11px] text-text-tertiary">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-brand" aria-hidden />
              Starting…
            </p>
          )}
          {progress?.progressPct != null ? <Progress value={progress.progressPct} className="h-1.5" /> : null}
          <div className="flex items-center justify-between text-[10px] text-text-tertiary">
            <span>{progress?.progressPct != null ? `${progress.progressPct}%` : ''}</span>
            {etaLabel ? <span>{etaLabel}</span> : null}
          </div>
        </div>
      ) : null}

      {/* Waiting and retrying are the two states that read as "stuck" when
          unexplained — say what is actually happening and that nothing is
          lost, so nobody refreshes or re-clicks out of doubt. */}
      {job.status === 'queued' ? (
        <p className="text-[10px] text-text-tertiary">
          Waiting its turn — heavier work runs one at a time. No action needed.
        </p>
      ) : null}
      {job.status === 'running' || job.status === 'recovering' ? (
        <p className="text-[10px] text-text-tertiary">
          Runs on the server — closing this tab or navigating away won't stop it.
        </p>
      ) : null}
      {job.status === 'cancelled' ? (
        <p className="text-[10px] text-text-tertiary">
          Stopped at your request. Steps that had finished were kept.
        </p>
      ) : null}
      {job.status === 'retrying' ? (
        <p className="text-[10px] text-text-tertiary">
          Hit a temporary problem. Progress is saved and it will retry on its own — no action needed.
        </p>
      ) : null}

      {job.status === 'failed' && job.error_message ? (
        <p className="text-[11px] text-destructive-text line-clamp-3">{job.error_message}</p>
      ) : null}

      {/* A job can complete with individual clinical steps broken. Name the
          steps and say plainly what is and isn't affected — "some parts
          didn't finish" with no detail is what sends a clinician hunting. */}
      {job.status === 'completed' && warnings.length > 0 ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/6 px-2 py-1.5">
          <p className="flex items-center gap-1 text-[11px] font-medium text-status-attention-text">
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
            {warnings.length === 1 ? '1 step needs review' : `${warnings.length} steps need review`}
          </p>
          <ul className="mt-1 space-y-0.5">
            {warnings.slice(0, 3).map((w, i) => {
              const label =
                PREPARE_STEP_LABELS.find(([key]) => key === w.step)?.[1] ?? jobTypeLabel(w.step);
              return (
                <li key={`${w.patient_id}-${w.step}-${i}`} className="text-[10px] text-text-tertiary">
                  <span className="text-sidebar-foreground">{label}</span> — {w.status}
                </li>
              );
            })}
            {warnings.length > 3 ? (
              <li className="text-[10px] text-text-tertiary">+{warnings.length - 3} more</li>
            ) : null}
          </ul>
          <p className="mt-1 text-[10px] text-text-tertiary">
            Everything that did finish is saved. Re-run Prepare to complete the rest.
          </p>
        </div>
      ) : null}

      {actionError ? <p className="text-[10px] text-destructive-text">{actionError}</p> : null}

      {(canCancel || canRetry) && (
        <div className="flex gap-1.5">
          {canCancel ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={handleCancel}
              className="h-6 flex-1 text-[10px]"
            >
              Cancel
            </Button>
          ) : null}
          {canRetry ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={handleRetry}
              className="h-6 flex-1 text-[10px]"
            >
              Retry
            </Button>
          ) : null}
        </div>
      )}

      {expanded ? (
        <div className="border-t border-sidebar-border pt-2">
          <PrepareStepsPanel job={job} patientNames={patientNames} />
          <EventTimeline jobId={job.job_id} />
        </div>
      ) : null}
    </li>
  );
}

export function JobCenterDrawer({ open, onClose }: JobCenterDrawerProps) {
  const { hydrating, connected, offline, hydrateError, refresh, acknowledgeAll, isAcknowledged } = useJobCenter();
  const navigate = useNavigate();
  const patientNames = usePatientNames(open);
  const [showOlder, setShowOlder] = useState(false);
  // Scope, and every count over it, come from the shared hook -- the launcher
  // badge reads the SAME numbers, so the two can no longer disagree.
  const { jobs, activeCount, inAdminConsole, awaitingPatient, isAdmin } = useScopedJobs();
  const { active } = useActivePatient();

  // WHERE "open this patient" GOES follows the same signal, because the two
  // twin pages are different products: the admin route carries build/reset
  // controls, the clinician route is read-only. Sending a clinician to the
  // admin URL dead-ends them on access-denied; sending an admin to the
  // read-only page strips the controls they opened it for.
  const openPatient = useCallback(
    (patientId: string) => {
      onClose();
      const id = encodeURIComponent(patientId);
      navigate(inAdminConsole ? `/admin/digital-twin/${id}` : `/patient/${id}/digital-twin`);
    },
    [navigate, onClose, inAdminConsole],
  );

  // The Sheet root stays mounted and `open` drives visibility, so Radix can
  // run its close sequence (exit animation + focus restore to the trigger).
  // Information hierarchy: what is happening now, what needs a human,
  // then the day-grouped history. A failure must never be buried under a
  // pile of completions with only a tint to find it by.
  const activeJobs = jobs.filter((j) => j.status === 'queued' || j.status === 'running' || j.status === 'retrying' || j.status === 'recovering');
  // Completed-with-warnings belongs here too: the clinical work is partly
  // missing, which is the whole point of the section — surfacing it only as a
  // tint inside the history is how a failed narrative went unnoticed.
  const needsAttention = jobs.filter(
    (j) =>
      !isAcknowledged(j.job_id) &&
      (j.status === 'failed' || (j.status === 'completed' && jobWarnings(j).length > 0)),
  );
  const recentJobs = jobs.filter((j) => !activeJobs.includes(j) && !needsAttention.includes(j));
  const recentGroups = groupByDay(recentJobs);
  // Keep the history scannable: show the two most recent days, collapse the rest
  // behind a toggle so a long backlog of completed jobs never buries what's live.
  const RECENT_VISIBLE_DAYS = 2;
  const visibleGroups = showOlder ? recentGroups : recentGroups.slice(0, RECENT_VISIBLE_DAYS);
  const olderCount = recentGroups
    .slice(RECENT_VISIBLE_DAYS)
    .reduce((n, [, g]) => n + g.length, 0);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className={cn(sidebar.root, 'w-[min(100vw,22rem)] max-w-full gap-0')}
      >
        <div className={sidebar.header}>
          <div className="min-w-0">
            {/* One name for both audiences. The scope is carried by the patient
                line below, not by renaming the panel: the accessible name is a
                stable contract (overlayA11y.drawers.test.tsx) and a drawer that
                calls itself something different per role is harder to describe
                in a handover, not easier. */}
            <SheetTitle className={sidebar.title}>Job Center</SheetTitle>
            {/* Say WHOSE work this is. Without it a clinician cannot tell a
                scoped list from an empty fleet, and would read "up to date" as
                "nothing is running anywhere". */}
            {!inAdminConsole && active ? (
              <p className="flex items-center gap-1 truncate text-[10px] text-text-tertiary">
                <User className="h-3 w-3 shrink-0" aria-hidden />
                <span className="truncate">
                  {active.patientName || patientNames.get(active.patientId) || `Patient ${shortId(active.patientId)}`}
                </span>
              </p>
            ) : null}
            <SheetDescription
              className={cn(
                'flex items-center gap-1 truncate text-[10px] normal-case tracking-normal',
                connected ? 'text-text-tertiary' : 'text-status-attention-text',
              )}
            >
              {offline ? (
                <>
                  <WifiOff className="h-3 w-3 shrink-0" aria-hidden />
                  Offline — jobs keep running on the server; will resync when back
                </>
              ) : connected ? (
                <>
                  <Wifi className="h-3 w-3 shrink-0" aria-hidden />
                  {activeCount > 0
                    ? `Live · ${activeCount} running`
                    : 'Live · up to date'}
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 shrink-0" aria-hidden />
                  Live feed paused — refreshing every 5s · {activeCount} running
                </>
              )}
            </SheetDescription>
          </div>
          <button type="button" onClick={onClose} className={sidebar.closeButton} aria-label="Close Job Center" title="Close Job Center">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className={`${sidebar.body} ${sidebar.bodyPad} flex flex-col gap-3`}>
          {/* Deliberately ROLE-based, unlike the list above: this targets
              /admin/jobs, which needs the admin role. An admin working in the
              clinical workspace still wants the shortcut to the full console;
              a clinician would only be routed into access-denied. */}
          {isAdmin ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/admin/jobs');
              }}
              className="self-end text-[11px] font-medium text-primary hover:underline"
            >
              View all jobs →
            </button>
          ) : null}
          {awaitingPatient ? (
            <div className="rounded-lg border border-dashed border-sidebar-border px-2.5 py-3 text-[11px]">
              <p className="flex items-center gap-1.5 text-sidebar-foreground">
                <User className="h-3 w-3 shrink-0" aria-hidden />
                No patient open
              </p>
              <p className="mt-1 text-text-tertiary">
                Open a patient to see their preparation jobs. Preparation starts
                automatically when a chart changes, and keeps running on the server
                whether or not this panel is open.
              </p>
            </div>
          ) : hydrating && jobs.length === 0 ? (
            <p className="rounded-lg border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2 text-[11px] text-text-tertiary">
              Loading jobs…
            </p>
          ) : hydrateError && jobs.length === 0 ? (
            <div className="rounded-lg border border-sidebar-border px-2.5 py-3 text-[11px]">
              <p className="flex items-center gap-1.5 text-status-negative-text">
                <XCircle className="h-3 w-3 shrink-0" aria-hidden />
                Couldn't load your jobs
              </p>
              <p className="mt-1 text-text-tertiary">
                Your jobs are safe on the server — this device just couldn't fetch the list.
              </p>
              <button
                type="button"
                onClick={() => void refresh()}
                className="mt-2 rounded-md border border-sidebar-border px-2 py-1 text-[11px] font-medium text-sidebar-foreground hover:bg-sidebar-accent"
              >
                Try again
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <p className="rounded-lg border border-dashed border-sidebar-border px-2 py-6 text-center text-[11px] text-text-tertiary">
              No background jobs yet. Long-running operations (Digital Twin builds, imports, exports) will show up here.
            </p>
          ) : (
            <>
              {activeJobs.length > 0 ? (
                <div className="space-y-1.5">
                  <div className={sidebar.sectionLabel}>Active ({activeJobs.length})</div>
                  <ul className="space-y-2">
                    {activeJobs.map((job) => (
                      <JobRow key={job.job_id} job={job} patientNames={patientNames} onOpenPatient={openPatient} />
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-sidebar-border px-2.5 py-2 text-[11px] text-text-tertiary">
                  {inAdminConsole
                    ? 'Nothing running right now — every patient is up to date. Preparation starts automatically when a chart changes.'
                    : 'Nothing running for this patient — their twin and summaries are up to date. Preparation starts automatically when the chart changes.'}
                </p>
              )}
              {needsAttention.length > 0 ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    {/* The section carries the worst severity present: an
                        outright failure outranks a partly-complete job. */}
                    {needsAttention.some((j) => j.status === 'failed') ? (
                      <div className={cn(sidebar.sectionLabel, 'mb-0 flex items-center gap-1 text-status-negative-text')}>
                        <XCircle className="h-3 w-3 shrink-0" aria-hidden />
                        Needs attention ({needsAttention.length})
                      </div>
                    ) : (
                      <div className={cn(sidebar.sectionLabel, 'mb-0 flex items-center gap-1 text-status-attention-text')}>
                        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                        Needs review ({needsAttention.length})
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={acknowledgeAll}
                      className="text-[10px] font-medium text-primary hover:underline"
                    >
                      Acknowledge all
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {needsAttention.map((job) => (
                      <JobRow key={job.job_id} job={job} patientNames={patientNames} onOpenPatient={openPatient} />
                    ))}
                  </ul>
                </div>
              ) : null}
              {visibleGroups.map(([label, group]) => (
                <div key={label} className="space-y-1.5">
                  <div className={sidebar.sectionLabel}>
                    {label} ({group.length})
                  </div>
                  <ul className="space-y-2">
                    {group.map((job) => (
                      <JobRow key={job.job_id} job={job} patientNames={patientNames} onOpenPatient={openPatient} />
                    ))}
                  </ul>
                </div>
              ))}
              {olderCount > 0 ? (
                <button
                  type="button"
                  onClick={() => setShowOlder((v) => !v)}
                  className="mt-1 w-full rounded-lg border border-dashed border-sidebar-border px-2.5 py-2 text-[11px] font-medium text-text-tertiary transition-colors hover:bg-sidebar-accent/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showOlder ? 'Hide older jobs' : `Show ${olderCount} older job${olderCount === 1 ? '' : 's'}`}
                </button>
              ) : null}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
